import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const DocsSectionContainer = styled.section`
  padding: 120px 5% 80px;
  background: #000000;
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const DocsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: left;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
`;

const DocsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 60px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const DocCard = styled(motion.div)`
  text-align: left;
`;

const DocIcon = styled.div`
  display: none;
`;

const DocTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const DocDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const DocLinks = styled.div`
  display: none;
`;

const DocLink = styled.a`
  display: none;
`;

const QuickStartSection = styled.div`
  display: none;
`;

const QuickStartTitle = styled.h3`
  display: none;
`;

const QuickStartDescription = styled.p`
  display: none;
`;

const QuickStartButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const QuickStartButton = styled.button`
  background: ${props => props.primary ? '#FF2300' : 'transparent'};
  border: 1px solid ${props => props.primary ? '#FF2300' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 8px;
  padding: 12px 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#e01f00' : 'rgba(255, 255, 255, 0.1)'};
    transform: translateY(-1px);
  }
`;

const DocsSection = () => {
  const docCategories = [
    {
      icon: '🚀',
      title: 'Getting Started',
      description: 'Learn the basics of DIZEE and get your first analytics dashboard up and running in minutes.',
      gradient: 'linear-gradient(135deg, #FF2300 0%, #FF6B35 100%)',
      links: [
        { title: 'Quick Start Guide', href: '#' },
        { title: 'Account Setup', href: '#' },
        { title: 'Connecting Platforms', href: '#' },
        { title: 'First Dashboard', href: '#' }
      ]
    },
    {
      icon: '📊',
      title: 'Analytics & Insights',
      description: 'Deep dive into DIZEE\'s analytics capabilities and learn how to interpret your music business data.',
      gradient: 'linear-gradient(135deg, #3498db 0%, #2ecc71 100%)',
      links: [
        { title: 'Understanding Metrics', href: '#' },
        { title: 'Custom Dashboards', href: '#' },
        { title: 'Revenue Tracking', href: '#' },
        { title: 'Fan Analytics', href: '#' }
      ]
    },
    {
      icon: '🤖',
      title: 'AI Features',
      description: 'Harness the power of AI to automate tasks, generate insights, and optimize your music business strategy.',
      gradient: 'linear-gradient(135deg, #9b59b6 0%, #e74c3c 100%)',
      links: [
        { title: 'AI Recommendations', href: '#' },
        { title: 'Content Generation', href: '#' },
        { title: 'Predictive Analytics', href: '#' },
        { title: 'Automation Setup', href: '#' }
      ]
    },
    {
      icon: '🔧',
      title: 'Integrations',
      description: 'Connect DIZEE with your existing tools and platforms for a seamless workflow experience.',
      gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
      links: [
        { title: 'Spotify Integration', href: '#' },
        { title: 'Social Media APIs', href: '#' },
        { title: 'Ticketing Platforms', href: '#' },
        { title: 'Third-party Tools', href: '#' }
      ]
    },
    {
      icon: '💼',
      title: 'Business Tools',
      description: 'Explore advanced features for labels, managers, and music industry professionals.',
      gradient: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
      links: [
        { title: 'Team Management', href: '#' },
        { title: 'Multi-Artist Dashboards', href: '#' },
        { title: 'Revenue Sharing', href: '#' },
        { title: 'White Label Options', href: '#' }
      ]
    },
    {
      icon: '🛠️',
      title: 'API Reference',
      description: 'Complete API documentation for developers building custom integrations with DIZEE.',
      gradient: 'linear-gradient(135deg, #16a085 0%, #27ae60 100%)',
      links: [
        { title: 'API Overview', href: '#' },
        { title: 'Authentication', href: '#' },
        { title: 'Endpoints', href: '#' },
        { title: 'SDKs & Libraries', href: '#' }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <DocsSectionContainer>
      <DocsContent>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Documentation & Resources
          </SectionTitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <DocsGrid>
            {docCategories.map((category, index) => (
              <DocCard key={index} variants={cardVariants}>
                <DocIcon gradient={category.gradient}>
                  {category.icon}
                </DocIcon>
                <DocTitle>{category.title}</DocTitle>
                <DocDescription>{category.description}</DocDescription>
                <DocLinks>
                  {category.links.map((link, linkIndex) => (
                    <DocLink key={linkIndex} href={link.href}>
                      {link.title}
                    </DocLink>
                  ))}
                </DocLinks>
              </DocCard>
            ))}
          </DocsGrid>
        </motion.div>

        <QuickStartSection>
          <QuickStartTitle>Ready to get started?</QuickStartTitle>
          <QuickStartDescription>
            Jump into DIZEE with our comprehensive onboarding guide, or explore our interactive 
            tutorials to see the platform in action.
          </QuickStartDescription>
          <QuickStartButtons>
            <QuickStartButton primary>
              Start Tutorial
            </QuickStartButton>
            <QuickStartButton>
              View All Docs
            </QuickStartButton>
            <QuickStartButton>
              Join Community
            </QuickStartButton>
          </QuickStartButtons>
        </QuickStartSection>
      </DocsContent>
    </DocsSectionContainer>
  );
};

export default DocsSection; 